import { useEffect, useMemo, useState } from "react"
import { createPortal } from "react-dom"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { ChevronLeft, ChevronRight, Plus, Trash2 } from "lucide-react"
import { toast } from "@/components/portal/Toast"

/**
 * Dienstplan des werkXpartner-Teams (#119) — der erste Punkt im Bereich „Intern".
 *
 * Marlene, 23.09.2026: „Wir brauchen einen internen Schichtplan." Paul,
 * 24.09.: fuer das werkXpartner-Team, „wann wer und wie lange den Dienst
 * uebernimmt"; keine Dienstarten, nur die Kategorien Dienst und Urlaub; der
 * Login-Name reicht. Nachtrag Paul: „bei dienstplan sollte jeden tag die
 * uhrzeit sichtbar sein 8-18 uhr um den dienst einzutragen".
 *
 * Die Woche als Zeitraster: eine Spalte je Tag, eine Zeile je Stunde von
 * 8 bis 18 Uhr. Dienste liegen als Bloecke in der Spalte, Urlaub steht in der
 * Ganztags-Zeile ueber dem Raster. Klick auf eine Stunde traegt genau dort
 * ein; Klick auf die Ganztags-Zeile traegt Urlaub ein. Wiederkehrende Dienste
 * als woechentliche Serie bis zu einem Enddatum — jede Woche bleibt einzeln
 * loeschbar.
 *
 * 🔴 Der Plan steuert nichts am Telefon-Bot. Ob und wann der abnimmt, steht
 * weiterhin im Voice-Admin (Betreuungszeiten).
 */

export type Dienst = {
  id: number
  benutzer: string
  datum: string
  von: string | null
  bis: string | null
  art: "dienst" | "urlaub"
  notiz: string | null
  serie_id: string | null
}

const TAGE = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"]
/** Sichtbares Tagesfenster: 8 bis 18 Uhr (Paul, 24.09.2026). */
export const TAG_START = 8
export const TAG_ENDE = 18
const STUNDEN = Array.from({ length: TAG_ENDE - TAG_START }, (_, i) => TAG_START + i)
const ZEILE_PX = 44

const iso = (d: Date) => {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, "0")
  const t = String(d.getDate()).padStart(2, "0")
  return `${y}-${m}-${t}`
}

/** Montag der Woche, in der `d` liegt (lokale Zeit, kein UTC-Versatz). */
export function montagVon(d: Date): Date {
  const m = new Date(d.getFullYear(), d.getMonth(), d.getDate())
  const wt = (m.getDay() + 6) % 7
  m.setDate(m.getDate() - wt)
  return m
}

const plusTage = (d: Date, n: number) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n)
const datumKurz = (d: Date) => `${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}.`
const hh = (h: number) => `${String(h).padStart(2, "0")}:00`

export function zeitText(e: Pick<Dienst, "art" | "von" | "bis">): string {
  if (e.art === "urlaub") return "Urlaub"
  if (e.von && e.bis) return `${e.von}–${e.bis}`
  if (e.von) return `ab ${e.von}`
  if (e.bis) return `bis ${e.bis}`
  return "ganzer Tag"
}

const minuten = (t: string | null): number | null => {
  if (!t) return null
  const m = /^(\d{1,2}):(\d{2})/.exec(t)
  return m ? parseInt(m[1]) * 60 + parseInt(m[2]) : null
}

/**
 * Lage eines Dienstes im Raster, in Pixeln ab 8 Uhr. Ohne Uhrzeit gilt der
 * ganze Tag; was vor 8 oder nach 18 Uhr liegt, wird an den Rand geschnitten —
 * der Eintrag bleibt sichtbar, nur nicht in voller Laenge.
 */
export function blockLage(e: Pick<Dienst, "von" | "bis">): { top: number; hoehe: number } {
  const fensterVon = TAG_START * 60
  const fensterBis = TAG_ENDE * 60
  const v = Math.max(fensterVon, Math.min(fensterBis, minuten(e.von) ?? fensterVon))
  const b = Math.max(fensterVon, Math.min(fensterBis, minuten(e.bis) ?? fensterBis))
  const top = ((v - fensterVon) / 60) * ZEILE_PX
  const hoehe = Math.max(18, ((Math.max(b, v + 15) - v) / 60) * ZEILE_PX)
  return { top, hoehe }
}

/** Ueberlappende Dienste eines Tages nebeneinander statt uebereinander. */
function spuren(eintraege: Dienst[]): Map<number, { spur: number; anzahl: number }> {
  const sortiert = [...eintraege].sort((a, b) => (minuten(a.von) ?? 0) - (minuten(b.von) ?? 0))
  const enden: number[] = []
  const lage = new Map<number, { spur: number; anzahl: number }>()
  for (const e of sortiert) {
    const v = minuten(e.von) ?? TAG_START * 60
    const b = minuten(e.bis) ?? TAG_ENDE * 60
    let spur = enden.findIndex((ende) => ende <= v)
    if (spur < 0) { spur = enden.length; enden.push(b) } else { enden[spur] = b }
    lage.set(e.id, { spur, anzahl: 0 })
  }
  const anzahl = Math.max(1, enden.length)
  lage.forEach((l) => { l.anzahl = anzahl })
  return lage
}

const PALETTE = ["#4f46e5", "#0891b2", "#059669", "#c026d3", "#ea580c", "#2563eb", "#7c3aed", "#0d9488"]
export const farbeFuer = (name: string, alle: string[]) => PALETTE[Math.max(0, alle.indexOf(name)) % PALETTE.length]

export default function DienstplanPage() {
  const qc = useQueryClient()
  const [montag, setMontag] = useState(() => montagVon(new Date()))
  const [dialog, setDialog] = useState<DialogVorgabe | null>(null)
  const von = iso(montag)
  const bis = iso(plusTage(montag, 6))

  const plan = useQuery<{ items: Dienst[]; ich: string }>({
    queryKey: ["dienstplan", von, bis],
    queryFn: async () => {
      const r = await fetch(`/api/v1/dienstplan?von=${von}&bis=${bis}`, { credentials: "include" })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      return r.json()
    },
  })
  const personen = useQuery<{ items: string[] }>({
    queryKey: ["dienstplan-personen"],
    queryFn: async () => {
      const r = await fetch("/api/v1/dienstplan/personen", { credentials: "include" })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      return r.json()
    },
    staleTime: 1000 * 60 * 10,
  })

  const frisch = () => qc.invalidateQueries({ queryKey: ["dienstplan"] })

  const loeschen = useMutation({
    mutationFn: async ({ id, serie }: { id: number; serie: boolean }) => {
      const r = await fetch(`/api/v1/dienstplan/${id}${serie ? "?serie=true" : ""}`, {
        method: "DELETE", credentials: "include",
      })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      return r.json() as Promise<{ geloescht: number }>
    },
    onSuccess: (d) => { toast(d.geloescht > 1 ? `${d.geloescht} Einträge gelöscht` : "Eintrag gelöscht", "success"); frisch() },
    onError: () => toast("Löschen fehlgeschlagen", "error"),
  })

  const items = plan.data?.items || []
  // Alle aktiven Zugaenge, dazu jeder, der in dieser Woche eingetragen ist —
  // ein Dienst darf nicht mit dem Zugang verschwinden.
  const alleNamen = useMemo(() => {
    const s = new Set<string>(personen.data?.items || [])
    items.forEach((e) => s.add(e.benutzer))
    return Array.from(s).sort((a, b) => a.localeCompare(b, "de"))
  }, [personen.data, items])

  const proTag = useMemo(() => {
    const m = new Map<string, { dienste: Dienst[]; urlaub: Dienst[] }>()
    for (const e of items) {
      const t = m.get(e.datum) ?? { dienste: [], urlaub: [] }
      if (e.art === "urlaub") t.urlaub.push(e); else t.dienste.push(e)
      m.set(e.datum, t)
    }
    return m
  }, [items])

  const heute = iso(new Date())
  const tage = Array.from({ length: 7 }, (_, i) => plusTage(montag, i))

  return (
    <div className="pp-main" style={{ padding: 24 }}>
      <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", flexWrap: "wrap", gap: 12, marginBottom: 16 }}>
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 600, marginBottom: 3 }}>Dienstplan</h1>
          <div style={{ fontSize: 12.5, color: "var(--text-muted)" }}>
            Wer wann Dienst hat — und wer im Urlaub ist. Nur für das werkXpartner-Team; der Telefon-Bot richtet sich nicht danach.
          </div>
        </div>
        <button type="button" className="pp-btn pp-btn-primary pp-btn-sm" onClick={() => setDialog({})}>
          <Plus className="w-3.5 h-3.5" /> Eintragen
        </button>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
        <button type="button" className="pp-btn pp-btn-sm" aria-label="Vorige Woche" onClick={() => setMontag((m) => plusTage(m, -7))}>
          <ChevronLeft className="w-4 h-4" />
        </button>
        <button type="button" className="pp-btn pp-btn-sm" onClick={() => setMontag(montagVon(new Date()))}>Heute</button>
        <button type="button" className="pp-btn pp-btn-sm" aria-label="Nächste Woche" onClick={() => setMontag((m) => plusTage(m, 7))}>
          <ChevronRight className="w-4 h-4" />
        </button>
        <span style={{ fontSize: 13, fontWeight: 600, marginLeft: 6 }} data-testid="wochenlabel">
          {datumKurz(montag)} – {datumKurz(plusTage(montag, 6))}{montag.getFullYear()}
        </span>
        <span style={{ marginLeft: "auto", display: "flex", gap: 10, alignItems: "center", fontSize: 12 }} data-testid="legende">
          <span style={{ fontWeight: 600, color: "var(--text-muted)" }}>Kategorien:</span>
          {alleNamen.map((n) => (
            <span key={n} style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
              <span style={{ width: 10, height: 10, borderRadius: 3, background: farbeFuer(n, alleNamen), display: "inline-block" }} />
              Dienst {n}
            </span>
          ))}
          <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
            <span style={{ width: 10, height: 10, borderRadius: 3, background: "rgba(245,158,11,0.9)", display: "inline-block" }} />
            Urlaub
          </span>
        </span>
      </div>

      <div className="pp-card" style={{ overflowX: "auto" }}>
        <div style={{ display: "grid", gridTemplateColumns: `56px repeat(7, minmax(120px, 1fr))`, fontSize: 12.5, minWidth: 900 }} data-testid="dienstplan-raster">
          {/* Kopfzeile */}
          <div style={{ borderBottom: "1px solid var(--border-color)" }} />
          {tage.map((t) => {
            const d = iso(t)
            return (
              <div key={d} style={{
                padding: "8px 6px", textAlign: "center", borderBottom: "1px solid var(--border-color)", borderLeft: "1px solid var(--border-color)",
                background: d === heute ? "rgba(99,102,241,0.10)" : undefined, fontWeight: 600,
              }}>
                <div>{TAGE[(t.getDay() + 6) % 7]}</div>
                <div style={{ fontSize: 11, color: "var(--text-muted)", fontWeight: 400 }}>{datumKurz(t)}</div>
              </div>
            )
          })}

          {/* Ganztags-Zeile: Urlaub */}
          <div style={{ padding: "6px 6px", fontSize: 10.5, color: "var(--text-muted)", borderBottom: "1px solid var(--border-color)", textAlign: "right" }}>Urlaub</div>
          {tage.map((t) => {
            const d = iso(t)
            const urlaub = proTag.get(d)?.urlaub || []
            return (
              <div
                key={d}
                data-testid={`ganztag-${d}`}
                onClick={() => setDialog({ datum: d, art: "urlaub" })}
                title="Klick: Urlaub eintragen"
                style={{
                  minHeight: 30, padding: 4, cursor: "pointer", display: "flex", flexDirection: "column", gap: 3,
                  borderBottom: "1px solid var(--border-color)", borderLeft: "1px solid var(--border-color)",
                  background: d === heute ? "rgba(99,102,241,0.05)" : undefined,
                }}
              >
                {urlaub.map((e) => (
                  <Block key={e.id} e={e} farbe="rgba(245,158,11,0.9)" onEdit={() => setDialog({ eintrag: e })} onDelete={(serie) => loeschen.mutate({ id: e.id, serie })} />
                ))}
              </div>
            )
          })}

          {/* Stundenraster 8–18 */}
          <div style={{ position: "relative", height: STUNDEN.length * ZEILE_PX }}>
            {STUNDEN.map((h, i) => (
              <div key={h} style={{ position: "absolute", top: i * ZEILE_PX - 7, right: 6, fontSize: 10.5, color: "var(--text-muted)" }}>{hh(h)}</div>
            ))}
            <div style={{ position: "absolute", top: STUNDEN.length * ZEILE_PX - 7, right: 6, fontSize: 10.5, color: "var(--text-muted)" }}>{hh(TAG_ENDE)}</div>
          </div>
          {tage.map((t) => {
            const d = iso(t)
            const dienste = proTag.get(d)?.dienste || []
            const lagen = spuren(dienste)
            return (
              <div
                key={d}
                data-testid={`tag-${d}`}
                style={{
                  position: "relative", height: STUNDEN.length * ZEILE_PX,
                  borderLeft: "1px solid var(--border-color)",
                  background: d === heute ? "rgba(99,102,241,0.04)" : undefined,
                }}
              >
                {STUNDEN.map((h, i) => (
                  <div
                    key={h}
                    data-testid={`slot-${d}-${h}`}
                    onClick={() => setDialog({ datum: d, von: hh(h), bis: hh(h + 1), art: "dienst" })}
                    title={`Klick: Dienst ${hh(h)}–${hh(h + 1)} eintragen`}
                    style={{
                      position: "absolute", left: 0, right: 0, top: i * ZEILE_PX, height: ZEILE_PX,
                      borderTop: "1px solid var(--border-color)", cursor: "pointer",
                    }}
                  />
                ))}
                {dienste.map((e) => {
                  const { top, hoehe } = blockLage(e)
                  const l = lagen.get(e.id) || { spur: 0, anzahl: 1 }
                  const breite = 100 / l.anzahl
                  return (
                    <div key={e.id} style={{ position: "absolute", top: top + 1, height: hoehe - 2, left: `calc(${breite * l.spur}% + 2px)`, width: `calc(${breite}% - 4px)` }}>
                      <Block e={e} farbe={farbeFuer(e.benutzer, alleNamen)} voll onEdit={() => setDialog({ eintrag: e })} onDelete={(serie) => loeschen.mutate({ id: e.id, serie })} />
                    </div>
                  )
                })}
              </div>
            )
          })}
        </div>
      </div>

      <div style={{ marginTop: 10, fontSize: 12, color: "var(--text-muted)" }}>
        Klick auf eine Stunde trägt dort einen Dienst ein, Klick auf die Urlaub-Zeile einen Urlaubstag. Klick auf einen Eintrag öffnet ihn zum Bearbeiten. Ein Dienst ohne Uhrzeit gilt für den ganzen Tag.
        Serien (↻) wiederholen sich wöchentlich; „Serie löschen“ nimmt nur diese und die folgenden Wochen mit.
      </div>

      {dialog && (
        <DienstDialog
          vorgabe={dialog}
          personen={alleNamen}
          ich={plan.data?.ich || ""}
          onClose={() => setDialog(null)}
          onGespeichert={() => { setDialog(null); frisch() }}
        />
      )}
    </div>
  )
}

function Block({ e, farbe, voll, onEdit, onDelete }: { e: Dienst; farbe: string; voll?: boolean; onEdit: () => void; onDelete: (serie: boolean) => void }) {
  return (
    <div
      // #222: Klick auf den Eintrag oeffnet ihn zum Bearbeiten — nicht den
      // Anlege-Dialog der Stunde darunter.
      onClick={(ev) => { ev.stopPropagation(); onEdit() }}
      title={[e.benutzer, zeitText(e), e.notiz, "Klick: bearbeiten"].filter(Boolean).join(" · ")}
      data-testid={`eintrag-${e.id}`}
      style={{
        display: "flex", alignItems: "flex-start", gap: 4, padding: "3px 6px", borderRadius: 6, fontSize: 11.5,
        background: farbe, color: "#fff", height: voll ? "100%" : undefined, boxSizing: "border-box", overflow: "hidden",
        boxShadow: "0 1px 2px rgba(0,0,0,0.15)", cursor: "pointer",
      }}
    >
      <span style={{ flex: 1, minWidth: 0, lineHeight: 1.25 }}>
        <span style={{ fontWeight: 600, display: "block", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{e.benutzer}{e.serie_id ? " ↻" : ""}</span>
        <span style={{ opacity: 0.9, whiteSpace: "nowrap" }}>{zeitText(e)}</span>
      </span>
      <button
        type="button"
        aria-label={`Eintrag ${e.benutzer} ${e.datum} löschen`}
        onClick={(ev) => {
          ev.stopPropagation()
          if (e.serie_id && window.confirm("Ganze Serie ab diesem Tag löschen?\n\nAbbrechen = nur diesen Tag.")) { onDelete(true); return }
          onDelete(false)
        }}
        style={{ background: "none", border: "none", cursor: "pointer", padding: 0, color: "rgba(255,255,255,0.85)", display: "flex", flexShrink: 0 }}
      >
        <Trash2 className="w-3 h-3" />
      </button>
    </div>
  )
}

/** Anlegen (Klick ins Raster) oder Bearbeiten (Klick auf einen Eintrag, #222). */
type DialogVorgabe = { datum?: string; von?: string; bis?: string; art?: "dienst" | "urlaub"; eintrag?: Dienst }

function DienstDialog({
  vorgabe, personen, ich, onClose, onGespeichert,
}: {
  vorgabe: DialogVorgabe
  personen: string[]
  ich: string
  onClose: () => void
  onGespeichert: () => void
}) {
  const alt = vorgabe.eintrag
  // Vorbelegt mit dem angemeldeten Nutzer — meistens traegt man sich selbst ein.
  const [benutzer, setBenutzer] = useState(alt?.benutzer || (personen.includes(ich) ? ich : (personen[0] || "")))
  const [datum, setDatum] = useState(alt?.datum || vorgabe.datum || iso(new Date()))
  const [art, setArt] = useState<"dienst" | "urlaub">(alt?.art || vorgabe.art || "dienst")
  const [von, setVon] = useState(alt?.von || vorgabe.von || "")
  const [bisZeit, setBisZeit] = useState(alt?.bis || vorgabe.bis || "")
  const [notiz, setNotiz] = useState(alt?.notiz || "")
  const [serie, setSerie] = useState(false)
  // Beim Bearbeiten einer Serie: nur dieser Tag oder diese und die folgenden
  // Wochen — dieselbe Wahl wie beim Loeschen.
  const [abHier, setAbHier] = useState(false)
  const [wiederholenBis, setWiederholenBis] = useState("")
  const [fehler, setFehler] = useState<string | null>(null)
  const [sende, setSende] = useState(false)

  useEffect(() => { if (!benutzer && personen[0]) setBenutzer(personen[0]) }, [personen, benutzer])

  const speichern = async () => {
    setFehler(null)
    if (!benutzer) { setFehler("Person fehlt"); return }
    if (!datum) { setFehler("Datum fehlt"); return }
    if (art === "dienst" && von && bisZeit && bisZeit <= von) { setFehler("„bis“ muss nach „von“ liegen"); return }
    if (serie && (!wiederholenBis || wiederholenBis <= datum)) { setFehler("Enddatum der Serie muss nach dem Starttag liegen"); return }
    setSende(true)
    try {
      if (alt) {
        const r = await fetch(`/api/v1/dienstplan/${alt.id}${abHier ? "?serie=true" : ""}`, {
          method: "PATCH", credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            benutzer, art, notiz: notiz.trim(),
            // Leer heisst „ganzer Tag" — "" setzt die Uhrzeit im Backend zurueck.
            von: art === "dienst" ? von : "",
            bis: art === "dienst" ? bisZeit : "",
            ...(abHier ? {} : { datum }),
          }),
        })
        if (!r.ok) {
          const d = await r.json().catch(() => ({}))
          throw new Error(d.error || `HTTP ${r.status}`)
        }
        const d = await r.json() as { geaendert?: number }
        toast((d.geaendert || 1) > 1 ? `${d.geaendert} Wochen geändert` : "Geändert", "success")
        onGespeichert()
        return
      }
      const r = await fetch("/api/v1/dienstplan", {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          benutzer, datum, art,
          von: art === "dienst" && von ? von : null,
          bis: art === "dienst" && bisZeit ? bisZeit : null,
          notiz: notiz.trim() || null,
          wiederholen_bis: serie ? wiederholenBis : null,
        }),
      })
      if (!r.ok) {
        const d = await r.json().catch(() => ({}))
        throw new Error(d.error || `HTTP ${r.status}`)
      }
      const d = await r.json() as { count: number }
      toast(d.count > 1 ? `${d.count} Wochen eingetragen` : "Eingetragen", "success")
      onGespeichert()
    } catch (e) {
      setFehler(e instanceof Error ? e.message : "Speichern fehlgeschlagen")
    } finally {
      setSende(false)
    }
  }

  const feld = {
    width: "100%", fontSize: 13, padding: "8px 10px", borderRadius: 7,
    border: "1px solid var(--border-color)", background: "var(--bg-card)",
    color: "var(--text)", outline: "none",
  } as const
  const beschriftung = {
    display: "block", fontSize: 11.5, fontWeight: 600, letterSpacing: 0.3,
    textTransform: "uppercase" as const, color: "var(--text-muted)", marginBottom: 4,
  }

  const titel = `${art === "urlaub" ? "Urlaub" : "Dienst"} ${alt ? "bearbeiten" : "eintragen"}`

  return createPortal(
    <div
      className="pp-overlay oben"
      onMouseDown={(ev) => { if (ev.target === ev.currentTarget) onClose() }}
      style={{
        position: "fixed", inset: 0, zIndex: 200, background: "rgba(15,23,42,0.35)",
        display: "flex", alignItems: "flex-start", justifyContent: "center", paddingTop: "12vh",
      }}
    >
      <div
        role="dialog"
        aria-label={titel}
        onMouseDown={(e) => e.stopPropagation()}
        style={{
          width: 440, maxWidth: "calc(100vw - 32px)", background: "var(--bg-card)", borderRadius: 12,
          border: "1px solid var(--border-color)", boxShadow: "0 18px 50px rgba(15,23,42,0.22)", overflow: "hidden",
        }}
      >
        <div style={{ padding: "12px 16px", borderBottom: "1px solid var(--border-color)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ fontSize: 14.5, fontWeight: 600 }}>{titel}</div>
          <button type="button" onClick={onClose} aria-label="Schließen"
                  style={{ background: "none", border: "none", cursor: "pointer", fontSize: 18, color: "var(--text-muted)" }}>×</button>
        </div>
        <form onSubmit={(e) => { e.preventDefault(); speichern() }} style={{ padding: 16, display: "flex", flexDirection: "column", gap: 12 }}>
          <div>
            <label style={beschriftung}>Kategorie</label>
            <div style={{ display: "flex", gap: 14, fontSize: 13 }}>
              <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <input type="radio" name="art" checked={art === "dienst"} onChange={() => setArt("dienst")} /> Dienst
              </label>
              <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <input type="radio" name="art" checked={art === "urlaub"} onChange={() => setArt("urlaub")} /> Urlaub
              </label>
            </div>
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <div style={{ flex: 1 }}>
              <label style={beschriftung}>Person</label>
              <select aria-label="Person" value={benutzer} onChange={(e) => setBenutzer(e.target.value)} style={feld}>
                {!personen.includes(benutzer) && benutzer && <option value={benutzer}>{benutzer}</option>}
                {personen.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div style={{ flex: 1 }}>
              <label style={beschriftung}>Datum</label>
              <input aria-label="Datum" type="date" value={datum} disabled={abHier} onChange={(e) => setDatum(e.target.value)} style={feld} />
            </div>
          </div>
          {art === "dienst" && (
            <div style={{ display: "flex", gap: 10 }}>
              <div style={{ flex: 1 }}>
                <label style={beschriftung}>Von</label>
                <input aria-label="Von" type="time" value={von} onChange={(e) => setVon(e.target.value)} style={feld} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={beschriftung}>Bis</label>
                <input aria-label="Bis" type="time" value={bisZeit} onChange={(e) => setBisZeit(e.target.value)} style={feld} />
              </div>
            </div>
          )}
          <div>
            <label style={beschriftung}>Notiz</label>
            <input aria-label="Notiz" value={notiz} onChange={(e) => setNotiz(e.target.value)} placeholder="optional" style={feld} />
          </div>
          {alt?.serie_id && (
            <div style={{ display: "flex", gap: 14, fontSize: 13 }}>
              <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <input type="radio" name="umfang" checked={!abHier} onChange={() => setAbHier(false)} /> Nur dieser Tag
              </label>
              <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <input type="radio" name="umfang" checked={abHier} onChange={() => { setAbHier(true); setDatum(alt.datum) }} /> Diese und folgende Wochen
              </label>
            </div>
          )}
          {!alt && <div style={{ display: "flex", flexDirection: "column", gap: 6, fontSize: 13 }}>
            <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <input type="checkbox" checked={serie} onChange={(e) => setSerie(e.target.checked)} /> Jede Woche wiederholen
            </label>
            {serie && (
              <div>
                <label style={beschriftung}>Bis einschließlich</label>
                <input aria-label="Wiederholen bis" type="date" value={wiederholenBis} onChange={(e) => setWiederholenBis(e.target.value)} style={feld} />
              </div>
            )}
          </div>}
          {fehler && <div style={{ fontSize: 12, color: "var(--destructive, #dc2626)" }}>{fehler}</div>}
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <button type="button" className="pp-btn pp-btn-sm" onClick={onClose}>Abbrechen</button>
            <button type="submit" className="pp-btn pp-btn-primary pp-btn-sm" disabled={sende}>{sende ? "Speichert …" : "Speichern"}</button>
          </div>
        </form>
      </div>
    </div>,
    document.body,
  )
}
